import { roundUpNumber } from './FormatNumber';

// CONSTANTS
const ML_TO_OZ_CONVERSION_VALUE = 29.574;
const ML_TO_L_CONVERSION_VALUE = 1000;
const TAX_TYPE_RATE_PER_NUM_OF_ML = 100;
const ONE_HUNDRED_PERCENT = 100;

/**
 * Converts ounces to milliliters
 * @param valueToConvert Ounces value that needs to be converted to milliliters
 * @returns Milliliters equivalent of parameter
 */
export const ouncesToMilliliters = (valueToConvert: string): number => parseFloat(valueToConvert) * ML_TO_OZ_CONVERSION_VALUE;

/**
 * Converts liters to milliliters
 * @param valueToConvert Liter value that needs to be converted to milliliters
 * @returns Milliliters equivalent of parameter
 */
export const litersToMilliliters = (valueToConvert: string): number => parseFloat(valueToConvert) * ML_TO_L_CONVERSION_VALUE;

/**
 * Calculates the excise tax amount
 * @param sizeOfBottle Amount entered into size of bottle input converted to milliliters
 * @param taxTypeRate Amount calculated based on chosen tax type
 * @param numOfBottles Amount entered into the bottles per case input
 * @returns Excise tax amount
 */
export const calculateExciseTax = (sizeOfBottle: number, taxTypeRate: number, numOfBottles: string): number => {
    return roundUpNumber((sizeOfBottle / TAX_TYPE_RATE_PER_NUM_OF_ML) * taxTypeRate * parseFloat(numOfBottles));
};

/**
 * Calculates the markup amount
 * @param markupPercent Markup percent amount from site builder config
 * @param landedCost Calculated landed cost amount
 * @returns Markup dollar amount
 */
export const calculateMarkup = (markupPercent: number, landedCost: number): number => {
    const test2 = roundUpNumber(landedCost * (markupPercent / ONE_HUNDRED_PERCENT));
    return test2;
};

/**
 * Calculates the landed cost amount for FOB
 * @param pricePerCase Amount entered into the price per case input
 * @param incomingFreight Incoming freight fee from data action based on chosen zone
 * @returns Landed cost amount for FOB
 */
export const calculateLandedCostFob = (pricePerCase: number, incomingFreight: number): number => pricePerCase + incomingFreight;

/**
 * Calculates the landed cost amount for retail
 * @param subTotal Calculated subtotal
 * @param markupPercent Markup percent amount from site builder config
 * @returns Landed cost amount for retail
 */
export const calculateLandedCostRetail = (subTotal: number, markupPercent: number): number =>
    subTotal / ((ONE_HUNDRED_PERCENT + markupPercent) / ONE_HUNDRED_PERCENT);

/**
 * Calculates the subtotal for FOB
 * @param landedCost Calculated landed cost amount
 * @param markupAmount Calculated markup dollar amount
 * @returns Subtotal for FOB
 */
export const calculateSubtotalFob = (landedCost: number, markupAmount: number): number => landedCost + markupAmount;

/**
 * Calculates the subtotal for retail
 * @param casePrice Amount entered into the price per case input
 * @param outgoingFreight Outgoing freight fee from site builder config
 * @param specialOrderFee Special order fee from site builder config
 * @param exciseTax Calculated excise tax
 * @returns Subtotal amount for retail
 */
export const calculateSubtotalRetail = (casePrice: number, outgoingFreight: number, specialOrderFee: number, exciseTax: number): number =>
    casePrice - (exciseTax + outgoingFreight + specialOrderFee);

/**
 * Calculates the case price for FOB
 * @param subTotal Calculated subtotal
 * @param outgoingFreight Outgoing freight fee from site builder config
 * @param specialOrderFee Special order fee from site builder config
 * @param exciseTax Calculated excise tax
 * @returns Case price amount for FOB
 */
export const calculateCasePriceFob = (subTotal: number, outgoingFreight: number, specialOrderFee: number, exciseTax: number): number =>
    subTotal + exciseTax + outgoingFreight + specialOrderFee;

/**
 * Calculates the price per case for retail
 * @param landedCost Calculated landed cost amount
 * @param incomingFreight Incoming freight fee from data action based on chosen zone
 * @returns Price per case amount for retail
 */
export const calculatePricePerCaseRetail = (landedCost: number, incomingFreight: number): number => landedCost - incomingFreight;
